import styled, { DefaultTheme } from 'styled-components';
import { Disableable, getSpacingToken, getTextTokenPreset, getToken, Size } from '@optimus-web/common';
import { DENSE_B2B_LIGHT_THEME } from '@optimus-web/theme';
import { Affix } from '../common/Affix';
import { Icon } from '../Icon';
import { Stack } from '../Stack';
import { getContainerPadding, getInputColor, getInputContainerHeight, getInputPadding } from './getInputStyles';

export interface InputContainerProps extends Disableable {
    inputSize?: Size;
    readOnly?: boolean;
}

export const InputContainer = styled.div<InputContainerProps>`
    height: calc(${getInputContainerHeight}px - ${getToken('borderWidth100')}px * 2);
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
    box-sizing: border-box;
`;

export const LeftSide = styled(Stack)<{ inputSize?: Size }>`
    padding-left: ${getContainerPadding}px;
`;

export const RightSide = styled(Stack)<{ inputSize?: Size }>`
    padding-right: ${getContainerPadding}px;
`;

export interface InputElementProps {
    alignRight?: boolean;
    disabled?: boolean;
    inputSize?: Size;
    size?: Size;
    readOnly?: boolean;
    hasLeftSide?: boolean;
    hasRightSide?: boolean;
    keyboardEntryDisabled?: boolean;
}

export const InputElement = styled.input<InputElementProps>`
    ${getTextTokenPreset('bodyMedium')};
    color: ${getInputColor};
    background-color: transparent;
    width: 100%;
    box-sizing: border-box;
    padding-top: ${getInputPadding}px;
    /* minus 1 beacuse the input inside the border is always an odd number and the border is an odd number, which equals to a positive number */
    padding-bottom: calc(${getInputPadding}px - 1px);
    padding-left: ${(props) => (props.hasLeftSide ? getSpacingToken('50') : getContainerPadding(props))}px;
    padding-right: ${(props) => (props.hasRightSide ? getSpacingToken('50') : getContainerPadding(props))}px;
    border: 0;
    border-radius: calc(${getSpacingToken('25')}px + ${getSpacingToken('50')}px);
    outline: none;
    display: block;
    margin: 0;
    appearance: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
    -webkit-text-fill-color: ${getInputColor};

    ::-webkit-date-and-time-value {
        text-align: start;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        appearance: none;
        margin: 0;
    }

    &[type='number'] {
        appearance: textfield;
    }

    &:invalid {
        box-shadow: none;
    }

    &:-moz-submit-invalid {
        box-shadow: none;
    }

    &:-moz-ui-invalid {
        box-shadow: none;
    }

    &::-ms-clear {
        display: none;
        width: 0;
        height: 0;
    }

    ${(props) =>
        props.keyboardEntryDisabled &&
        `
        caret-color: transparent;
    `}

    text-align: ${(props) => (props.alignRight ? 'right' : 'left')};

    &:-webkit-autofill {
        background-color: ${getToken('backgroundStaticFlat')};
        -webkit-text-fill-color: ${getInputColor};
        box-shadow: 0 0 0 900px ${getToken('backgroundStaticFlat')} inset;
    }
`;

export const InputWrapper = styled.div`
    width: 100%;
    position: relative;
    justify-content: center;
    align-items: center;
    display: flex;
`;

export const Prefix = styled(Affix)``;

export const Suffix = styled(Affix)`
    margin-left: ${getSpacingToken('50')}px;
`;

export interface PlaceholderElementProps extends Disableable {
    children?: React.ReactNode;
    inputSize?: Size;
    disabled?: boolean;
    $readOnly?: boolean;
    hasLeftSide?: boolean;
    hasRightSide?: boolean;
}

export const PlaceholderElement = styled.div<PlaceholderElementProps>`
    ${getTextTokenPreset('bodyMedium')};
    color: ${({ disabled }) => (disabled ? getToken('textDisabled') : getToken('textStaticTertiary'))};
    position: absolute;
    pointer-events: none;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    box-sizing: border-box;
    padding-left: ${(props) => (props.hasLeftSide ? getSpacingToken('50') : getContainerPadding(props))}px;
    padding-right: ${(props) => (props.hasRightSide ? getSpacingToken('50') : getContainerPadding(props))}px;
`;

export const getPaymentInputStyles = (themeProvided: DefaultTheme = DENSE_B2B_LIGHT_THEME) => `
    ${getTextTokenPreset('bodyMedium')({ theme: themeProvided })};
    color: ${getInputColor({ theme: themeProvided })};
    border: 0;
    display: block;
    outline: none;
    margin: 0;
    padding: 0 ${getSpacingToken('200')({ theme: themeProvided })}px;
    font-feature-settings: 'calt', 'tnum', 'ss01', 'ss03', 'cv01', 'cv02', 'cv03', 'cv04', 'cv05', 'cv06', 'cv07',
        'cv08', 'cv09', 'cv10', 'zero';
`;

export const IndicativeIconWrapper = styled(Icon)<{ disabled?: boolean }>`
    padding-right: ${getSpacingToken('50')}px;
    color: ${({ disabled }) => (disabled ? getToken('textDisabled') : getToken('textStaticPrimary'))};
`;
